import React from "react"
import { ResponsivePie } from "@nivo/pie"
import { ResponsiveBar } from "@nivo/bar"
import ChartCard from "@react/projects/chantiers_dashboard/components/ChartCard"
import PieLegend from "@react/projects/chantiers_dashboard/components/PieLegend"
import { CHART_COLORS, NIVO_THEME, PALETTE, formatShort } from "@react/projects/chantiers_dashboard/components/palette"

const CenterMetric = ({ centerX, centerY, total, label }) => (
  <g transform={`translate(${centerX}, ${centerY})`}>
    <text textAnchor="middle" dominantBaseline="central" fill="#0f172a"
          style={{ fontSize: 22, fontWeight: 700, fontFamily: "inherit" }}>
      {total.toLocaleString("fr-FR")}
    </text>
    <text textAnchor="middle" dominantBaseline="central" fill="#64748b" y={18}
          style={{ fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
      {label}
    </text>
  </g>
)

const StatusPie = ({ data }) => {
  const total = data.reduce((acc, d) => acc + d.value, 0)
  const coloredData = data.map((d, idx) => ({ ...d, color: CHART_COLORS[idx % CHART_COLORS.length] }))
  const legendItems = coloredData.map(d => ({ id: d.id, label: d.label, value: d.value, color: d.color }))

  return (
    <ChartCard
      icon  = "file-invoice-dollar"
      title = {I18n.t("quotes.project_index.charts.by_status", { defaultValue: "Devis par statut" })}
      empty = {total === 0 ? I18n.t("quotes.project_index.charts.empty", { defaultValue: "Aucun devis" }) : null}
    >
      <div className="h-full flex items-center px-5 pb-4 pt-3">
        <div className="shrink-0 relative" style={{ width: 210, height: 210 }}>
          <ResponsivePie
            data                    = {coloredData}
            margin                  = {{ top: 4, right: 4, bottom: 4, left: 4 }}
            innerRadius             = {0.68}
            padAngle                = {1.5}
            cornerRadius            = {3}
            activeOuterRadiusOffset = {6}
            colors                  = {{ datum: "data.color" }}
            borderWidth             = {2}
            borderColor             = "#fff"
            enableArcLinkLabels     = {false}
            enableArcLabels         = {false}
            theme                   = {NIVO_THEME}
            layers                  = {[
              "arcs",
              "arcLabels",
              "arcLinkLabels",
              "legends",
              (props) => (
                <CenterMetric
                  centerX = {props.centerX}
                  centerY = {props.centerY}
                  total   = {total}
                  label   = {I18n.t("quotes.project_index.devis", { defaultValue: "Devis" })}
                />
              ),
            ]}
          />
        </div>
        <div className="flex-1 self-center min-w-0 pl-3 overflow-hidden">
          <PieLegend items={legendItems} />
        </div>
      </div>
    </ChartCard>
  )
}

const TopClients = ({ rows }) => {
  const data = (rows || []).map(r => ({
    client: r.name?.slice(0, 26) || "—",
    value:  Math.round((r.value || 0) / 100),
    count:  r.count,
  }))
  const empty = data.length === 0 || data.every(d => d.value === 0)

  return (
    <ChartCard
      icon  = "users"
      title = {I18n.t("quotes.project_index.charts.top_clients", { defaultValue: "Top clients (montant HT)" })}
      empty = {empty ? I18n.t("quotes.project_index.charts.empty", { defaultValue: "Aucune donnée" }) : null}
      height = {320}
    >
      {!empty && (
        <div className="h-full px-3 pb-3 pt-2">
          <ResponsiveBar
            data        = {data}
            keys        = {["value"]}
            indexBy     = "client"
            layout      = "horizontal"
            margin      = {{ top: 8, right: 32, bottom: 30, left: 150 }}
            padding     = {0.35}
            colors      = {PALETTE.violet}
            borderRadius = {3}
            theme       = {NIVO_THEME}
            axisBottom  = {{ tickValues: 5, format: (v) => formatShort(v * 100) }}
            axisLeft    = {{ tickSize: 0, tickPadding: 8 }}
            labelTextColor = "#fff"
            label       = {(d) => formatShort(d.value * 100)}
            labelSkipWidth = {60}
            tooltip     = {(d) => (
              <div className="bg-slate-900 text-white text-[11px] px-2 py-1.5 rounded shadow">
                <div className="font-semibold mb-0.5">{d.indexValue}</div>
                <div>{formatShort(d.value * 100)} · {d.data.count} devis</div>
              </div>
            )}
            enableGridX = {true}
            enableGridY = {false}
          />
        </div>
      )}
    </ChartCard>
  )
}

const MonthlyChart = ({ rows }) => {
  const data = (rows || []).map(r => ({
    month: r.label,
    count: r.count,
    value: Math.round((r.value || 0) / 100),
  }))
  const empty = data.every(d => d.count === 0)

  return (
    <ChartCard
      icon  = "chart-column"
      title = {I18n.t("quotes.project_index.charts.monthly", { defaultValue: "Devis émis (12 mois)" })}
      empty = {empty ? I18n.t("quotes.project_index.charts.empty", { defaultValue: "Aucune donnée" }) : null}
      height = {320}
    >
      {!empty && (
        <div className="h-full px-3 pb-3 pt-2">
          <ResponsiveBar
            data        = {data}
            keys        = {["value"]}
            indexBy     = "month"
            margin      = {{ top: 12, right: 16, bottom: 36, left: 56 }}
            padding     = {0.3}
            colors      = {PALETTE.indigo}
            borderRadius = {3}
            theme       = {NIVO_THEME}
            axisBottom  = {{ tickSize: 0, tickPadding: 6, tickRotation: -25 }}
            axisLeft    = {{ tickSize: 0, tickPadding: 6, format: (v) => formatShort(v * 100) }}
            enableLabel = {false}
            tooltip     = {(d) => (
              <div className="bg-slate-900 text-white text-[11px] px-2 py-1.5 rounded shadow">
                <div className="font-semibold mb-0.5">{d.indexValue}</div>
                <div>{formatShort(d.value * 100)}</div>
                <div className="text-slate-300">{d.data.count} devis</div>
              </div>
            )}
            enableGridY = {true}
            enableGridX = {false}
          />
        </div>
      )}
    </ChartCard>
  )
}

const ChartsRow = ({ statusBreakdown, topClients, monthly }) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5">
    <div className="lg:col-span-1">
      <StatusPie data={statusBreakdown || []} />
    </div>
    <div className="lg:col-span-2 grid grid-cols-1 xl:grid-cols-2 gap-3">
      <TopClients rows={topClients} />
      <MonthlyChart rows={monthly} />
    </div>
  </div>
)

export default ChartsRow
